<script lang="ts">
  // A task in the conversation (maquette TaskChat.dc.html): status, title, description,
  // requester → assignee, due date, documents. A click opens its detail on the right.
  import type { Message, TaskInfo } from '../lib/api';
  import { render } from '../lib/markdown';
  import { router, threadPath } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import { dueText, isOverdue, statusText } from '../lib/tasks';
  import Avatar from './Avatar.svelte';

  let { message, task }: { message: Message; task: TaskInfo } = $props();
  const html = $derived(message.body ? render(message.body, app.me?.username ?? '') : '');
</script>

<a
  class="card"
  href={threadPath(message.conversation_id, message.id)}
  onclick={(e) => {
    e.preventDefault();
    router.go(threadPath(message.conversation_id, message.id));
  }}
>
  <span class="top">
    <span class="pill {task.display_status}">{statusText(task)}</span>
    <span class="kind">Tâche</span>
  </span>
  <span class="title" class:done={task.status === 'done'}>{task.title}</span>
  {#if html}<span class="desc">{@html html}</span>{/if}
  <span class="meta">
    <span class="people" title="Demandée par {task.requester.display_name}{task.assignee ? `, responsable ${task.assignee.display_name}` : ', sans responsable'}">
      <Avatar user={task.requester} size={22} />
      <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      {#if task.assignee}<Avatar user={task.assignee} size={22} />{:else}<span class="nobody">Non attribuée</span>{/if}
      <span class="sr-only">Demandée par {task.requester.display_name}, {task.assignee ? `responsable ${task.assignee.display_name}` : 'sans responsable'}</span>
    </span>
    <span class="due" class:late={isOverdue(task)}>
      <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
      {dueText(task)}
    </span>
    {#if task.document_count > 0}
      <span class="docs">
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H6v18h12V7z" /><path d="M14 3v4h4" /></svg>
        {task.document_count} document{task.document_count > 1 ? 's' : ''}
      </span>
    {/if}
  </span>
</a>

<style>
  .card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 480px;
    margin-top: 6px;
    padding: 12px 14px;
    background: var(--bg);
    border: 1px solid var(--accent-border);
    border-left: 0;
    border-radius: 10px;
    box-shadow: inset 4px 0 0 var(--accent);
    color: var(--ink);
    text-decoration: none;
  }
  .card:hover {
    border-color: var(--accent);
  }
  .top {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .kind {
    font-size: 12px;
    color: var(--muted);
  }
  .title {
    font-weight: 600;
    font-size: 15px;
    overflow-wrap: anywhere;
  }
  .title.done {
    color: var(--muted);
    text-decoration: line-through;
  }
  .desc {
    color: var(--ink-2);
    overflow-wrap: anywhere;
  }
  .desc :global(p) {
    margin: 0;
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 13px;
    color: var(--ink-2);
  }
  .people,
  .due,
  .docs {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .people svg {
    color: var(--muted);
  }
  .nobody {
    font-size: 12px;
    color: var(--muted);
  }
  .due.late {
    color: var(--danger);
    font-weight: 600;
  }
</style>
